import { Component, Input, inject } from '@angular/core';
import { CommonModule, DecimalPipe } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { CompanyCurrencyPipe } from 'app/pipes/currency.pipe';
import { DecimalConfigService } from 'app/services/decimal-config.service';
import { CellRenderer, CellRendererParams } from 'app/shared/components/smart-grid/core/interfaces/cell-renderer.interface';

@Component({
  selector: 'app-currency-comparison-cell-renderer',
  standalone: true,
  imports: [CommonModule, MatIconModule, MatTooltipModule],
  template: `
    <div class="flex items-center gap-2 justify-end">
      <span>{{ formattedAfterValue }}</span>
      @if (hasChanged) {
        <mat-icon
          class="text-orange-500"
          [matTooltip]="'Modificado: ' + formattedBeforeValue + ' → ' + formattedAfterValue"
          style="font-size: 16px; width: 16px; height: 16px;">
          change_circle
        </mat-icon>
      }
    </div>
  `,
  styles: [`
    :host {
      display: flex;
      width: 100%;
    }
  `]
})
export class CurrencyComparisonCellRendererComponent<T> implements CellRenderer<T, number> {
  /**
   * Parameters from the grid containing the cell value and row data
   */
  @Input() params!: CellRendererParams<T, number>;

  // Usar inject() en lugar de constructor DI
  private readonly decimalPipe = new DecimalPipe('en-US');
  private readonly companyCurrencyPipe = inject(CompanyCurrencyPipe);
  private readonly decimalConfigService = inject(DecimalConfigService);

  private formatCurrency(value: number): string {
    if (value == null) return '-';
    const decimals = this.decimalConfigService.getDecimals();
    const formattedNum = this.decimalPipe.transform(value, `1.${decimals}-${decimals}`) ?? '-';
    if (formattedNum === '-') return '-';
    const symbol = this.companyCurrencyPipe.transform('symbol');
    return `${symbol} ${formattedNum}`;
  }

  get beforeValue(): number {
    const beforeField = this.params?.colDef?.cellRendererParams?.beforeField as string;
    return this.params?.data?.[beforeField];
  }

  get afterValue(): number {
    const afterField = this.params?.colDef?.cellRendererParams?.afterField as string;
    return this.params?.data?.[afterField];
  }

  get formattedBeforeValue(): string {
    return this.formatCurrency(this.beforeValue);
  }

  get formattedAfterValue(): string {
    return this.formatCurrency(this.afterValue);
  }

  get hasChanged(): boolean {
    if (this.beforeValue === this.afterValue) return false;
    if (this.beforeValue == null && this.afterValue == null) return false;
    if (this.beforeValue == null || this.afterValue == null) return true;
    return Math.abs(this.beforeValue - this.afterValue) > 0.001;
  }
}
